import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { LOADING } from '../../../redux_store/actionTypes';
import { getRideHistoryById } from '../../../redux_store/rides/ride_thunk';
import { convertFromPesewasToCedis, displayFile, getUserStars } from '../../../utils';
import Pagination from '../../templates/Pagination';
import CustomDateRangePicker from '../../templates/modals/CustomDateRangePicker';
import LoadingModal from '../../templates/modals/LoadingModal';
import { Calendar, EyeEmpty, PinAlt } from 'iconoir-react';

const UserTripHistoryPage = () => {
  const dispatch = useDispatch();
  const { rides, status, user } = useSelector((state) => state.ride);
  const profilePicRef = useRef(null);
  const [tripTotalAmt, setTripTotalAmt] = useState(0)
  const [dateRangeVal, setDateRangeVal] = useState('');
  const [showRangePicker, setShowRangePicker] = useState(false);
  const [tripHistory, setTripHistory] = useState([]);
  const { id } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;
  const totalPages = Math.ceil(tripHistory.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  useEffect(() => {
    if (id) dispatch(getRideHistoryById(id));
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    displayFile(user.profile_pic, profilePicRef.current)
  }, [user]);

  const getStatusBadge = (status) => {
    const styles = {
      'PENDING': 'badge-warning',
      'TO PICKUP': 'badge bg-slate-500 text-white',
      'AT PICKUP': 'badge bg-slate-700 text-white',
      'TO DESTINATION': 'badge-primary',
      'AT DESTINATION': 'badge bg-cyan-600 text-white',
      'COMPLETED': 'badge-success',
      'RATED': 'badge bg-orange-500 text-white',
      'CANCELLED': 'badge-danger',
    };

    const labels = {
      'PENDING': 'Pending',
      'TO PICKUP': 'To Pickup',
      'AT PICKUP': 'At Pickup',
      'TO DESTINATION': 'To Destination',
      'AT DESTINATION': 'At Destination',
      'COMPLETED': 'Completed',
      'RATED': 'Rated',
      'CANCELLED': 'Cancelled',
    };

    return (
      <span className={styles[status] || 'badge bg-slate-400 text-white'}>
        {labels[status] || 'Unknown'}
      </span>
    );
  };

  const extractAmount = (rides) => {
    let amount = 0;
    rides && rides.forEach((ride) => {
      if (ride.final_total_cost)
        amount += convertFromPesewasToCedis(ride.final_total_cost);
    });
    return amount;
  }

  useEffect(() => {
    if (Array.isArray(rides)) {
      const amount = extractAmount(rides);
      setTripTotalAmt(amount);
      setTripHistory(rides);
    }
  }, [rides]);

  const handleRangeSelect = (range) => {
    const customHistory = [];
    setShowRangePicker(false);
    let { startDate, endDate } = range.selection;
    startDate = moment(startDate);
    endDate = moment(endDate);
    setDateRangeVal(`${startDate.format('LL')} - ${endDate.format('LL')}`)
    rides.forEach((trip) => {
      const tripDate = moment(trip.createdAt);
      if (tripDate.isBetween(startDate, endDate)) {
        customHistory.push(trip);
      }
    });
    const amount = extractAmount(customHistory);
    setTripTotalAmt(amount);
    setTripHistory(customHistory);
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
      {status === LOADING && <LoadingModal message={'Loading...'} />}
      {showRangePicker && <CustomDateRangePicker onRangeSelect={handleRangeSelect} />}

      {/* Profile Card */}
      <div className='lg:col-span-2'>
        <div className='card space-y-4'>
          <h3 className='text-lg font-semibold text-surface-800'>Profile</h3>
          <div ref={profilePicRef} className='aspect-square rounded-2xl bg-surface-100 overflow-hidden'></div>
          <div className='text-center space-y-1'>
            <span className='badge-primary capitalize'>{user.role}</span>
            <p className='font-semibold text-surface-800 mt-2'>
              {user.role === 'driver' ? 'Total Earnings' : 'Total Expenses'}
            </p>
            <p className='text-2xl font-bold text-indigo-600'>GH₵ {tripTotalAmt.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Trip History */}
      <div className='lg:col-span-4 space-y-6'>
        <div className='card'>
          <div className='flex items-center justify-between mb-4'>
            <h3 className='text-lg font-semibold text-surface-800'>
                {user.surname}'s Ride History
              </h3>
            <div className='relative'>
              <input
                onClick={() => setShowRangePicker(true)}
                type='text'
                  value={dateRangeVal}
                readOnly
                className='input pl-10 cursor-pointer'
                placeholder='Select date range'
              />
              <Calendar strokeWidth={2} className='w-4 h-4 text-surface-400 absolute left-3 top-1/2 -translate-y-1/2' />
            </div>
          </div>

          <div className='overflow-x-auto -mx-6'>
            <table className="table-modern">
                <thead>
                <tr>
                  {user.role === 'customer' ? <th>Driver</th> : <th>Customer</th>}
                  <th>From</th>
                  <th>To</th>
                  <th>Status</th>
                  <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tripHistory && tripHistory.length > 0 ?
                  tripHistory.slice(startIndex, endIndex).map((trip, index) => (
                    <tr key={trip.id || index}>
                      {user.role === 'customer' ? (
                        <td>
                          <div className="flex items-center gap-3">
                            <img
                              className="w-10 h-10 rounded-xl object-cover ring-2 ring-surface-100"
                              src={trip?.driver?.profile_pic}
                              alt=""
                              loading="lazy"
                            />
                            <div>
                              <p className="font-semibold text-surface-800">
                                {trip?.driver?.surname} {trip?.driver?.other_names}
                              </p>
                              <p className="text-xs text-surface-400 flex items-center gap-1">
                                {getUserStars(trip?.driver?.ratings)}
                              </p>
                            </div>
                          </div>
                        </td>
                      ) : (
                        <td>
                          <div className="flex items-center gap-3">
                            <img
                              className="w-10 h-10 rounded-xl object-cover ring-2 ring-surface-100"
                              src={trip.customer.profile_pic}
                              alt=""
                              loading="lazy"
                            />
                            <div>
                              <p className="font-semibold text-surface-800">
                                {trip.customer.surname} {trip.customer.other_names}
                              </p>
                              <p className="text-xs text-surface-400 flex items-center gap-1">
                                {getUserStars(trip.customer.ratings)}
                              </p>
                            </div>
                          </div>
                        </td>
                      )}
                      <td>
                        <p className='max-w-[120px] truncate' title={trip.origin_description}>
                          {trip.origin_description}
                        </p>
                      </td>
                      <td>
                        <p className='max-w-[120px] truncate' title={trip.destination_description}>
                          {trip.destination_description}
                        </p>
                      </td>
                      <td>
                        {getStatusBadge(trip.status)}
                      </td>
                      <td>
                        <Link
                          to={`/trips/${trip.id}`}
                          className='btn-ghost p-2 rounded-lg inline-flex'
                        >
                          <EyeEmpty strokeWidth={2} className='w-4 h-4' />
                        </Link>
                      </td>
                    </tr>
                  )) :
                  <tr>
                    <td colSpan={5} className="text-center py-12">
                      <div className='flex flex-col items-center gap-3'>
                        <div className='w-16 h-16 rounded-2xl bg-surface-100 flex items-center justify-center'>
                          <PinAlt strokeWidth={1.5} className='w-8 h-8 text-surface-400' />
                        </div>
                        <div>
                          <p className='font-medium text-surface-600'>No trips found</p>
                          <p className='text-sm text-surface-400'>No ride history available</p>
                        </div>
                      </div>
                    </td>
                    </tr>
                  }
                </tbody>
            </table>
                  </div>

          {totalPages > 1 && (
            <div className='mt-4'>
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default UserTripHistoryPage
